'use client'

import { useState, useRef } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  IconFileText,
  IconLoader2,
  IconX,
  IconArrowLeft
} from '@tabler/icons-react'
import { useCreateReaderContent } from '@/hooks/useReaderContents'
import {
  convertTextToEditableContent,
  convertPdfPagesToEditableContent,
  convertEditableToDocumentContent
} from '@/lib/text-processor'
import type { EditableDocumentContent } from '@/types/documents'
import { useTranslations } from 'next-intl'
import { BodyText } from '@/components/ui/typography'
import { DocumentEditor } from './document-editor'
import { useEntitlements } from '@/hooks/useEntitlements'
import { useOffline } from '@/components/offline-context'
import { useRouter } from '@/i18n/routing'
import { analytics } from '@/lib/analytics'
import { processPlainTextToReaderContent } from '@/lib/reader/process'
import { plainDocumentContentToReaderContent } from '@/lib/reader/document-adapter'

export function DocumentUpload({ onSuccess }: { onSuccess?: () => void }) {
  const t = useTranslations('reader')
  const router = useRouter()
  const createReaderContent = useCreateReaderContent()
  const { isOfflineEnabled, isReady } = useOffline()
  const { limits } = useEntitlements()
  const maxFileSize = limits.maxDocumentSizeKb * 1024
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [title, setTitle] = useState('')
  const [textInput, setTextInput] = useState('')
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [processing, setProcessing] = useState(false)
  const [editableContent, setEditableContent] =
    useState<EditableDocumentContent | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [showEditor, setShowEditor] = useState(false)
  const [tokenizeProgress, setTokenizeProgress] = useState<{
    completed: number
    total: number
  } | null>(null)

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Check file size
    if (file.size > maxFileSize) {
      setError(
        t('fileTooLarge', {
          maxSize: `${(maxFileSize / 1024 / 1024).toFixed(1)}MB`,
          defaultValue: `File too large. Maximum size is ${(maxFileSize / 1024 / 1024).toFixed(1)}MB.`
        })
      )
      return
    }

    setSelectedFile(file)
    setError(null)
    setProcessing(true)

    try {
      if (file.type === 'application/pdf' || file.name.endsWith('.pdf')) {
        // Process PDF with pages preserved
        const pdfProcessor = await import('@/lib/pdf-processor')
        const pages = await pdfProcessor.processPdfFileWithPages(file)

        // Validate that pages is an array
        if (!Array.isArray(pages)) {
          throw new Error(
            'Invalid response from PDF processor. Please try again or rebuild the worker file.'
          )
        }

        const editable = convertPdfPagesToEditableContent(pages)
        setEditableContent(editable)
      } else {
        // Process as text file
        const text = await file.text()
        const editable = convertTextToEditableContent(text)
        setEditableContent(editable)
      }

      if (!title) {
        // Auto-generate title from filename
        const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '')
        setTitle(nameWithoutExt)
      }

      setShowEditor(true)
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t('fileProcessingError', {
              defaultValue: 'Failed to process file'
            })
      )
      setSelectedFile(null)
    } finally {
      setProcessing(false)
    }
  }

  const handleTextInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.target.value
    setTextInput(text)
    setError(null)
  }

  const handleProcessText = () => {
    if (!textInput.trim()) {
      setError(
        t('textRequired', {
          defaultValue: 'Please enter some text'
        })
      )
      return
    }

    try {
      const editable = convertTextToEditableContent(textInput)
      setEditableContent(editable)
      setShowEditor(true)
    } catch {
      setError(
        t('textProcessingError', {
          defaultValue: 'Failed to process text'
        })
      )
    }
  }

  const handleSave = async () => {
    if (!title.trim()) {
      setError(
        t('titleRequired', {
          defaultValue: 'Please enter a document title'
        })
      )
      return
    }

    if (!editableContent) {
      setError(
        t('noContent', {
          defaultValue: 'No content to save'
        })
      )
      return
    }

    const content = convertEditableToDocumentContent(editableContent)

    if (content.paragraphs.length === 0) {
      setError(
        t('noSelectedContent', {
          defaultValue: 'Please select at least one page or block to save'
        })
      )
      return
    }

    const fileType = editableContent.sourceType
    const fileName = selectedFile?.name
    const fileSize = selectedFile?.size

    // Validate content structure
    if (
      !content ||
      typeof content !== 'object' ||
      !Array.isArray(content.paragraphs)
    ) {
      console.error('Invalid content structure:', content)
      setError(
        t('invalidContentStructure', {
          defaultValue:
            'Invalid content structure. Please try processing the document again.'
        })
      )
      return
    }

    try {
      setError(null) // Clear any previous errors
      setProcessing(true)
      setTokenizeProgress({ completed: 0, total: 0 })
      const readerContentId =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : `reader-${Date.now()}`
      let readerContent
      try {
        readerContent = await processPlainTextToReaderContent(
          readerContentId,
          title.trim(),
          content,
          {
            isOffline: isOfflineEnabled && isReady,
            onProgress: setTokenizeProgress
          }
        )
      } catch (processingError) {
        console.warn(
          'Reader tokenization failed, saving readable fallback',
          processingError
        )
        readerContent = plainDocumentContentToReaderContent(
          readerContentId,
          title.trim(),
          content
        )
      }

      const saved = await createReaderContent.mutateAsync({
        ...readerContent,
        title: title.trim(),
        metadata: {
          ...readerContent.metadata,
          fileName,
          fileType,
          createdAt: new Date().toISOString()
        },
        fileSize
      })

      analytics.readerImportCompleted(
        readerContent.sourceType,
        readerContent.blocks.length,
        Object.keys(readerContent.glossary).length,
        fileType
      )

      // Reset form
      setTitle('')
      setTextInput('')
      setSelectedFile(null)
      setEditableContent(null)
      setShowEditor(false)
      setError(null)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
      setTokenizeProgress(null)

      onSuccess?.()
      router.push(`/read/document/${saved.id}`)
    } catch (error) {
      // Error is handled by the mutation (toast), but also set local error state
      console.error('Failed to save document:', error)
      setError(
        error instanceof Error
          ? error.message
          : t('documentCreateError', {
              defaultValue: 'Failed to create document'
            })
      )
    } finally {
      setProcessing(false)
    }
  }

  const handleCancel = () => {
    setShowEditor(false)
    setEditableContent(null)
    setError(null)
  }

  const handleStartOver = () => {
    setTitle('')
    setTextInput('')
    setSelectedFile(null)
    setEditableContent(null)
    setShowEditor(false)
    setError(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleRemoveFile = () => {
    setSelectedFile(null)
    setEditableContent(null)
    setShowEditor(false)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  // Show editor if content is ready
  if (showEditor && editableContent) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>{t('uploadDocument')}</CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleStartOver}
              disabled={processing || createReaderContent.isPending}
            >
              <IconArrowLeft className="mr-2 h-4 w-4" />
              {t('startOver', { defaultValue: 'Start Over' })}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Title input */}
          <div>
            <Label htmlFor="title-input">{t('documentTitle')}</Label>
            <Input
              id="title-input"
              className="mt-2"
              placeholder={t('titlePlaceholder')}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          {/* Document Editor */}
          <DocumentEditor
            content={editableContent}
            onContentChange={setEditableContent}
            onSave={handleSave}
            onCancel={handleCancel}
            isSaving={createReaderContent.isPending || processing}
          />

          {tokenizeProgress && (
            <div className="bg-muted/30 text-muted-foreground rounded-lg border p-3 text-sm">
              Building instant word sheets
              {tokenizeProgress.total > 0
                ? ` (${tokenizeProgress.completed}/${tokenizeProgress.total})`
                : '...'}
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="bg-destructive/10 text-destructive border-destructive/20 rounded-lg border p-3">
              <BodyText className="text-sm">{error}</BodyText>
            </div>
          )}
        </CardContent>
      </Card>
    )
  }

  // Show input form
  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {t('uploadDocument', { defaultValue: 'Turn text into a reader' })}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Unified input layout */}
        <div className="grid gap-6 md:grid-cols-2">
          {/* Text input */}
          <div className="space-y-2">
            <Label htmlFor="text-input">{t('pasteText')}</Label>
            <Textarea
              id="text-input"
              className="min-h-32"
              placeholder={t('textPlaceholder', {
                defaultValue: 'Paste Chinese text you want to read...'
              })}
              value={textInput}
              onChange={handleTextInput}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleProcessText}
              disabled={!textInput.trim() || processing}
              className="w-full"
            >
              <IconFileText className="mr-2 h-4 w-4" />
              {t('processText', { defaultValue: 'Process Text' })}
            </Button>
          </div>

          {/* File upload */}
          <div className="space-y-2">
            <Label htmlFor="file-input">{t('uploadFile')}</Label>
            <div className="space-y-2">
              <Input
                ref={fileInputRef}
                id="file-input"
                type="file"
                accept=".pdf,.txt,.text"
                onChange={handleFileSelect}
                disabled={processing}
              />
              <BodyText className="text-muted-foreground text-xs">
                {t('fileTypesHint', {
                  defaultValue: 'PDF or text files'
                })}{' '}
                (Max {(maxFileSize / 1024 / 1024).toFixed(1)}MB)
              </BodyText>
            </div>

            {selectedFile && (
              <div className="bg-muted/50 rounded-lg border p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <IconFileText className="h-4 w-4" />
                    <BodyText className="text-sm">{selectedFile.name}</BodyText>
                    <BodyText className="text-muted-foreground text-xs">
                      ({(selectedFile.size / 1024).toFixed(1)} KB)
                    </BodyText>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    onClick={handleRemoveFile}
                    disabled={processing}
                  >
                    <IconX className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}

            {processing && (
              <div className="flex items-center gap-2 text-sm">
                <IconLoader2 className="h-4 w-4 animate-spin" />
                <BodyText>
                  {t('processingFile', {
                    defaultValue: 'Processing file...'
                  })}
                </BodyText>
              </div>
            )}
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div className="bg-destructive/10 text-destructive border-destructive/20 rounded-lg border p-3">
            <BodyText className="text-sm">{error}</BodyText>
          </div>
        )}

        {/* Info text */}
        <div className="bg-muted/30 rounded-lg border p-3">
          <BodyText className="text-muted-foreground text-sm">
            {t('uploadHint', {
              defaultValue:
                'Paste or upload Chinese text. Miaozi turns it into a calm reader where you can tap words, save useful ones, and review later.'
            })}
          </BodyText>
        </div>
      </CardContent>
    </Card>
  )
}
